import React from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { SeverityChip } from "../common/StatusChip";
import PlaybookIntent from "./PlaybookIntent";

const formatDuration = (seconds) => {
  if (seconds == null || seconds === "") return null;
  const n = Number(seconds);
  if (Number.isNaN(n)) return null;
  if (n === 0) return "Under 1 min";
  if (n < 60) return `~${n}s`;
  return `~${Math.ceil(n / 60)} min`;
};

/**
 * Renders browse-time playbook metadata from GET /remediation/checks/{check_id}.
 * `consequences` is the primary customer-facing copy (per API contract).
 */
const PlaybookSummary = ({ playbook }) => {
  if (!playbook) return null;

  const durationLabel = formatDuration(playbook.estimated_seconds);
  const hasConsequences = Array.isArray(playbook.consequences) && playbook.consequences.length > 0;
  const hasSideEffects = Array.isArray(playbook.side_effects) && playbook.side_effects.length > 0;
  const hasIntent = playbook.intent != null || Boolean(playbook.intent_prose?.trim?.());

  return (
    <Box sx={{ mb: 2, p: 1.5, backgroundColor: "grey.50", borderRadius: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: hasConsequences || hasSideEffects || hasIntent ? 1.25 : 0, flexWrap: "wrap", gap: 1 }}
      >
        {playbook.severity && <SeverityChip severity={playbook.severity} />}
        {playbook.service_name && (
          <Chip
            size="small"
            label={`Service: ${playbook.service_name}`}
            sx={{ backgroundColor: "white", textTransform: "lowercase" }}
          />
        )}
        {durationLabel && (
          <Chip size="small" label={durationLabel} sx={{ backgroundColor: "white" }} />
        )}
        {playbook.reversibility && (
          <Chip
            size="small"
            label={`Reversibility: ${playbook.reversibility}`}
            sx={{ backgroundColor: "white" }}
          />
        )}
        {playbook.blast_radius && (
          <Chip
            size="small"
            label={`Blast radius: ${playbook.blast_radius}`}
            sx={{ backgroundColor: "white" }}
          />
        )}
      </Stack>

      {hasConsequences && (
        <Alert severity="info" sx={{ py: 1, mb: hasSideEffects || hasIntent ? 1.25 : 0, "& .MuiAlert-message": { width: "100%" } }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
            What this fix changes
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13, lineHeight: 1.5 }}>
            {playbook.consequences.map((line, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{line}</li>
            ))}
          </Box>
        </Alert>
      )}

      {hasSideEffects && (
        <Box sx={{ mb: hasIntent ? 1.25 : 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Additional caveats
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12.5, lineHeight: 1.45 }}>
            {playbook.side_effects.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </Box>
        </Box>
      )}

      {hasIntent && (
        <PlaybookIntent intent={playbook.intent} intentProse={playbook.intent_prose} />
      )}
    </Box>
  );
};

export default PlaybookSummary;
